import { z } from 'zod';
import { ABN_REGEX } from './constants.js';
import { DocumentTaxTypeSchema } from './invoices.js';
import { PaymentOptionSchema } from './payments.js';
import { emptyToUndefined, optionalPhone } from './utils.js';
const optionalStr = z
    .string()
    .optional()
    .transform(emptyToUndefined)
    .pipe(z.string().optional());
/**
 * Rejects calendar-invalid dates (e.g. `2024-02-30`) that still pass the `yyyy-MM-dd`
 * format check. Guards against `Invalid Date` before calling `toISOString`, which throws
 * a raw `RangeError` instead of a validation issue.
 */
function isValidCalendarDate(val) {
    const parsed = new Date(`${val}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(val);
}
/** Request body for updating the organisation's business profile. */
export const UpdateOrganizationBusinessSchema = z.object({
    name: optionalStr,
    email: z
        .string({ error: 'Email is required' })
        .trim()
        .min(1, 'Email is required')
        .email(),
    phone: optionalPhone,
    address: optionalStr,
    country: z
        .string()
        .optional()
        .transform(emptyToUndefined)
        .pipe(z.literal('AU').optional()),
    timezone: z
        .string()
        .optional()
        .transform(emptyToUndefined)
        .pipe(z
        .string()
        .refine((tz) => Intl.supportedValuesOf('timeZone').includes(tz), 'Invalid IANA timezone')
        .optional()),
});
/** Supported tax identifier types. */
export const TaxIdTypeSchema = z.enum([
    'ABN',
    'GST',
    'VAT',
    'EIN',
    'HST',
    'GSTIN',
    'Other',
]);
/**
 * Request body for updating the organisation's tax settings. Cross-field rules: -
 * taxIdCustomLabel is required when taxIdType is 'Other', and must be absent otherwise. -
 * defaultTaxRate is required when defaultTaxType is 'CUSTOM'. - Setting defaultTaxType to
 * null clears both default tax fields.
 */
export const UpdateOrganizationTaxSchema = z
    .object({
    taxIdType: TaxIdTypeSchema.optional(),
    taxIdValue: optionalStr,
    taxIdCustomLabel: optionalStr,
    defaultTaxType: DocumentTaxTypeSchema.nullable().optional(),
    defaultTaxRate: z.number().min(0).max(1).nullable().optional(),
    gstRegistered: z.boolean().nullable().optional(),
    gstRegisteredFrom: z.iso
        .date()
        .nullable()
        .optional()
        .refine((val) => val == null || isValidCalendarDate(val), {
        error: '"Registered since" must be a valid calendar date',
    })
        .transform((val) => val == null ? val : `${val}T00:00:00.000Z`)
        .pipe(z.string().nullable().optional()),
})
    .superRefine((data, ctx) => {
    if (data.taxIdType === 'Other' && !data.taxIdCustomLabel) {
        ctx.addIssue({
            code: 'custom',
            message: 'Custom label is required when Tax ID type is "Other"',
            path: ['taxIdCustomLabel'],
        });
    }
    if (data.taxIdType !== 'Other' && data.taxIdCustomLabel) {
        ctx.addIssue({
            code: 'custom',
            message: 'Custom label is only used when Tax ID type is "Other"',
            path: ['taxIdCustomLabel'],
        });
    }
    if (data.taxIdType === 'ABN' && data.taxIdValue && !ABN_REGEX.test(data.taxIdValue)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Must be a valid ABN (e.g. 12 345 678 901)',
            path: ['taxIdValue'],
        });
    }
    if (data.defaultTaxType === 'CUSTOM' &&
        (data.defaultTaxRate === undefined || data.defaultTaxRate === null)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Tax rate is required when tax type is Custom',
            path: ['defaultTaxRate'],
        });
    }
    // Only enforced when both fields are present in the same request — the frontend
    // always submits them together, so this can't be bypassed via that form.
    if (data.gstRegistered === true &&
        data.defaultTaxType !== undefined &&
        data.defaultTaxType !== 'GST') {
        ctx.addIssue({
            code: 'custom',
            message: 'Default tax type must be GST when registered for GST',
            path: ['defaultTaxType'],
        });
    }
    // gstRegisteredFrom is null both when unset and when the user picked the "I'm not
    // sure" escape hatch — the UI enforces one-or-the-other on Yes, not this schema.
    if (data.gstRegistered !== true && data.gstRegisteredFrom) {
        ctx.addIssue({
            code: 'custom',
            message: '"Registered since" date is only used when GST registered is Yes',
            path: ['gstRegisteredFrom'],
        });
    }
});
/**
 * Request body for replacing all payment options on the organisation, plus the default
 * payment terms (days after issue date that payment is due) applied to new
 * invoices/quotes.
 */
export const UpdateOrganizationPaymentOptionsSchema = z.object({
    paymentOptions: z.array(PaymentOptionSchema),
    paymentTermsDays: z.number().int().min(1).max(365),
});
/**
 * Validates a single domain string — no protocol, no path, no port. Accepts labels
 * separated by dots (e.g. "acme.com", "mail.acme.co.uk").
 */
export const DomainSchema = z
    .string()
    .regex(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, 'Must be a valid domain (e.g. acme.com)');
/** Validates a single CIDR block — accepts both IPv4 and IPv6 notation. */
export const CidrSchema = z.union([z.cidrv4(), z.cidrv6()]);
/** Request body for updating the organisation's IP/domain allowlist. */
export const UpdateOrganizationAccessSchema = z.object({
    allowedDomains: z.array(DomainSchema),
    allowedCidrs: z.array(CidrSchema),
});
/** Request body for updating the organisation's invoice number prefix. */
export const UpdateOrganizationInvoicingSchema = z.object({
    invoiceNumberPrefix: z
        .string()
        .regex(/^[A-Z0-9-]{1,10}$/, 'Prefix must be 1-10 characters: A-Z, 0-9, or "-"'),
});
/** Request body for bumping the tenant's next invoice number. */
export const SetNextInvoiceNumberSchema = z.object({
    nextNumber: z.number().int().positive(),
});
/**
 * Request body for accepting the onboarding wizard's Terms of Service gate. `accepted`
 * must be `true` — there's no way to submit or store a declined/unaccepted state, and the
 * server stamps the timestamp/version/acceptor itself rather than trusting the client.
 */
export const UpdateOrganizationTermsSchema = z.object({
    accepted: z.literal(true),
});
//# sourceMappingURL=organization.js.map